import { useContext, useMemo } from 'react'
import * as React from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'

const ballCycle = keyframes`
  0% { transform: translateX(var(--loading-animation-start-x)) translateY(-50%) scale(0); }
  25% { transform: translateX(var(--loading-animation-start-x)) translateY(-50%) scale(1); }
  50% { transform: translateX(var(--loading-animation-middle-x)) translateY(-50%) scale(1); }
  75% { transform: translateX(var(--loading-animation-end-x)) translateY(-50%) scale(1); }
  100% { transform: translateX(var(--loading-animation-end-x)) translateY(-50%) scale(0); }
`

const numBalls = 4
const period = 3.1

const Ball = styled.div`
  --loading-animation-start-x: 25%;
  --loading-animation-middle-x: 250%;
  --loading-animation-end-x: 475%;

  position: absolute;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.brand.primary.normal};
  /* Keep the absolute animation origin physical so RTL only changes the travel direction. */
  left: 0;
  top: 50%;
  animation: ${ballCycle} ${period}s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;

  [dir="rtl"] & {
    --loading-animation-start-x: 475%;
    --loading-animation-end-x: 25%;
  }

  &.one {
    animation-delay: ${-period / numBalls}s;
  }

  &.two {
    animation-delay: ${-2 * period / numBalls}s;
  }

  &.three {
    animation-delay: ${-3 * period / numBalls}s;
  }

  &.four {
    animation-delay: ${-4 * period / numBalls}s;
  }
`

const Root = styled.div<{ size: number }>`
  display: inline-block;
  position: relative;
  width: ${({ size }) => rem(size * 6)};
  height: ${({ size }) => rem(size * 2)};

  &.paused > div {
    animation-play-state: paused;
  }
`

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  size?: number
  paused?: boolean
}

function LoadingAnimation(props: Props) {
  const { size = 16, paused, className, ...rest } = props

  const playState = useContext(LoaderPlayStateContext)
  const ballStyle = useMemo(() => ({ height: rem(size), width: rem(size) }), [size])

  return (
    <Root
      {...rest}
      size={size}
      data-testid="loading-animation"
      className={clsx(
        className,
        { paused: paused || playState === 'paused' },
      )}
    >
      <Ball style={ballStyle} className="one" />
      <Ball style={ballStyle} className="two" />
      <Ball style={ballStyle} className="three" />
      <Ball style={ballStyle} className="four" />
    </Root>
  )
}

export default React.memo(LoadingAnimation)
